import {Box} from 'src/design/atoms/Box';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import React from 'react';
import type {GetUserActivitySummaryRequest} from 'src/api/ProactApiClient';
import {NoticePopover} from 'src/design/molecules/NoticePopover';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SidebarLinkPersonnel} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {useGetUserActivitySummaryQuery} from 'src/redux/queries/userActivity';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {PersonnelDocumentTitle} from '../../../../../../app/personnel/personnelHelpers';
import {UserActivityHeatmap} from './UserActivityHeatmap';
import {useUserActivityColumns} from './useUserActivityColumns';

export const UserActivityList: React.FC = () => {
  useSetDocumentTitle(PersonnelDocumentTitle);

  const columns = useUserActivityColumns();

  const tableState = useTableState({
    identifier: 'personnel-activity',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'lastActiveAt', desc: true}],
    defaultShowColumnFilters: true,
  });

  const request: GetUserActivitySummaryRequest = {
    start: tableState.start,
    length: tableState.length,
    filters: tableState.filters,
    filterFns: tableState.filterFns,
    sorting: tableState.sorting,
  };

  const {data, isLoading, isError, isFetching, refetch} =
    useGetUserActivitySummaryQuery(request, {refetchOnMountOrArgChange: true});

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns,
      data: rows,
      enableRowActions: false,
      renderDetailPanel: ({row}) => (
        <Box px="xl" py="md">
          <UserActivityHeatmap userId={row.original.userId} />
        </Box>
      ),
      renderEmptyRowsFallback: () => (
        <Text c="dimmed" ta="center" py="xl">
          No user activity data available yet. Activity is recorded as users
          access the platform.
        </Text>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <ContentWrapper>
      <Stack gap="xs">
        <PageHeader>
          <PageHeader.Title icon={SidebarLinkPersonnel.icon}>
            User Activity
          </PageHeader.Title>
        </PageHeader>
        <NoticePopover
          label="How activity is tracked"
          title="How Activity Is Tracked"
          description='Activity is recorded when a user accesses any part of the platform. It is updated at most once per hour. "Last Active" shows the most recent access, and "Days Active (30d)" counts distinct calendar days with at least one access in the last 30 days.'
          secondaryText='"Never" means the user has not accessed the platform since activity tracking was enabled.'
        />
        {table}
      </Stack>
    </ContentWrapper>
  );
};
